import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useSessionTimeout, setStoredTimeout } from "./hooks/useSessionTimeout";
import Dashboard from "./pages/Dashboard";
import ConnectorsPage from "./pages/ConnectorsPage";
import HistoryPage from "./pages/HistoryPage";
import SchedulesPage from "./pages/SchedulesPage";
import AuditPage from "./pages/AuditPage";
import UsersPage from "./pages/UsersPage";
import InvitePage from "./pages/InvitePage";
import SettingsPage from "./pages/SettingsPage";
import RestorePage from "./pages/RestorePage";
import AdminTenantsPage from "./pages/AdminTenantsPage";
import AdminTenantDetailPage from "./pages/AdminTenantDetailPage";
import "./styles/global.css";

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { token } = useAuth();
  return token ? <>{children}<SessionTimeoutManager /></> : <Navigate to="/login" replace />;
}

function SessionTimeoutManager() {
  const { logout } = useAuth();
  const navigate   = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const { showWarning, secondsLeft, stayLoggedIn } = useSessionTimeout(handleLogout);

  if (!showWarning) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999,
      background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: 380, background: "var(--surface)",
        border: "1px solid var(--border2)", borderRadius: "var(--r2)",
        padding: 28, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏱</div>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Still there?</h2>
        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>
          You'll be logged out due to inactivity in
        </p>
        <div style={{
          fontSize: 36, fontWeight: 700, fontFamily: "var(--mono)",
          color: secondsLeft <= 10 ? "#DC2626" : "var(--teal)",
          margin: "12px 0",
        }}>
          {secondsLeft}s
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={handleLogout}>Log out now</button>
          <button className="btn btn-primary" onClick={stayLoggedIn}>Stay logged in</button>
        </div>
      </div>
    </div>
  );
}

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  // MFA step
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode]   = useState("");

  // MFA-setup handoff: when /auth/login returns mfa_setup_required, we
  // reuse the SignupPage flow by stashing the signup_token + otpauth data
  // and flipping into a setup view.
  const [setupPayload, setSetupPayload] = useState<{
    signup_token: string;
    otpauth_uri:  string;
    secret:       string;
  } | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [setupRecoveryCodes, setSetupRecoveryCodes] = useState<string[] | null>(null);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      // Call login endpoint directly to detect MFA
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) { setError(data.error ?? "Login failed"); return; }

      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setMfaCode("");
        return;
      }

      if (data.mfa_setup_required) {
        setSetupPayload({
          signup_token: data.signup_token,
          otpauth_uri:  data.mfa.otpauth_uri,
          secret:       data.mfa.secret,
        });
        setSetupCode("");
        return;
      }

      // No MFA — complete login
      localStorage.setItem("fb_access_token", data.access_token);
      localStorage.setItem("fb_refresh_token", data.refresh_token);
      window.location.href = "/";
    } catch (err: any) {
      setError(err.message ?? "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupPayload) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/signup-complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_token: setupPayload.signup_token, code: setupCode }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Verification failed"); return; }

      localStorage.setItem("fb_access_token", data.access_token);
      localStorage.setItem("fb_refresh_token", data.refresh_token);
      setSetupRecoveryCodes(data.recovery_codes ?? []);
    } catch (err: any) {
      setError(err.message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/mfa-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_token: mfaToken, code: mfaCode }),
      });
      const data = await res.json();

      if (!res.ok) { setError(data.error ?? "Invalid code"); return; }

      localStorage.setItem("fb_access_token", data.access_token);
      localStorage.setItem("fb_refresh_token", data.refresh_token);
      window.location.href = "/";
    } catch (err: any) {
      setError(err.message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width:"100%", padding:"9px 12px", border:"1px solid var(--border2)", borderRadius:"var(--r)", fontSize:14, fontFamily:"var(--sans)", outline:"none", background:"var(--surface)" };
  const labelStyle = { fontSize:12, fontFamily:"var(--mono)", color:"var(--text3)", display:"block" as const, marginBottom:6, textTransform:"uppercase" as const, letterSpacing:"0.06em" };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--surface2)" }}>
      <div style={{ width:360, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--r2)", padding:"32px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:28 }}>
          <div style={{ width:32, height:32, background:"var(--ink)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ width:20, height:20, background:"var(--teal)", borderRadius:4 }} />
          </div>
          <div>
            <div style={{ fontWeight:500, fontSize:15, letterSpacing:"-0.3px" }}>FinBackup</div>
            <div style={{ fontSize:11, fontFamily:"var(--mono)", color:"var(--text3)" }}>Financial Data Protection</div>
          </div>
        </div>

        {!mfaToken && !setupPayload && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:20, letterSpacing:"-0.3px" }}>Sign in</h2>
            {error && <div style={{ background:"var(--red-bg)", color:"#791F1F", fontSize:13, padding:"10px 14px", borderRadius:"var(--r)", marginBottom:16 }}>{error}</div>}
            <form onSubmit={handlePasswordSubmit} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)} required style={inputStyle} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading} style={{ marginTop:8, width:"100%", padding:"10px" }}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <div style={{ marginTop:18, textAlign:"center", fontSize:13, color:"var(--text3)" }}>
              New to FinBackup?{" "}
              <a
                href="/signup"
                onClick={(e) => { e.preventDefault(); navigate("/signup"); }}
                style={{ color:"var(--teal)", textDecoration:"none", fontWeight:500 }}
              >
                Create an account
              </a>
            </div>
          </>
        )}

        {mfaToken && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:8, letterSpacing:"-0.3px" }}>Two-factor authentication</h2>
            <p style={{ fontSize:13, color:"var(--text3)", marginBottom:20 }}>
              Enter the 6-digit code from your authenticator app, or a recovery code.
            </p>
            {error && <div style={{ background:"var(--red-bg)", color:"#791F1F", fontSize:13, padding:"10px 14px", borderRadius:"var(--r)", marginBottom:16 }}>{error}</div>}
            <form onSubmit={handleMfaSubmit} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={labelStyle}>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  placeholder="123456 or XXXX-XXXX"
                  required
                  style={{ ...inputStyle, fontSize: 18, fontFamily:"var(--mono)", textAlign:"center", letterSpacing:"0.15em" }}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading || !mfaCode.trim()} style={{ marginTop:8, width:"100%", padding:"10px" }}>
                {loading ? "Verifying…" : "Verify"}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => { setMfaToken(null); setMfaCode(""); setError(""); }} style={{ width:"100%", padding:"8px", fontSize:12 }}>
                ← Back to login
              </button>
            </form>
          </>
        )}

        {setupPayload && !setupRecoveryCodes && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:6, letterSpacing:"-0.3px" }}>Finish setting up two-factor auth</h2>
            <p style={{ fontSize:13, color:"var(--text3)", marginBottom:16 }}>
              MFA is required. Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy) to activate your account.
            </p>
            {error && <div style={{ background:"var(--red-bg)", color:"#791F1F", fontSize:13, padding:"10px 14px", borderRadius:"var(--r)", marginBottom:12 }}>{error}</div>}
            <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}>
              <img
                alt="MFA QR code"
                width={180}
                height={180}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupPayload.otpauth_uri)}`}
                style={{ border:"1px solid var(--border2)", borderRadius:"var(--r)", padding:6, background:"#fff" }}
              />
            </div>
            <div style={{ fontSize:12, color:"var(--text3)", textAlign:"center", marginBottom:4 }}>
              Can't scan? Enter this secret manually:
            </div>
            <div style={{ fontFamily:"var(--mono)", fontSize:12, textAlign:"center", letterSpacing:"0.05em", wordBreak:"break-all", color:"var(--text2)", marginBottom:18 }}>
              {setupPayload.secret}
            </div>
            <form onSubmit={handleSetupVerify} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={labelStyle}>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={setupCode}
                  onChange={e => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  required
                  style={{ ...inputStyle, fontSize: 18, fontFamily:"var(--mono)", textAlign:"center", letterSpacing:"0.15em" }}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading || setupCode.length < 6} style={{ marginTop:8, width:"100%", padding:"10px" }}>
                {loading ? "Verifying…" : "Verify & finish"}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => { setSetupPayload(null); setSetupCode(""); setError(""); }} style={{ width:"100%", padding:"8px", fontSize:12 }}>
                ← Back to login
              </button>
            </form>
          </>
        )}

        {setupPayload && setupRecoveryCodes && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:6, letterSpacing:"-0.3px" }}>Save your recovery codes</h2>
            <p style={{ fontSize:13, color:"var(--text3)", marginBottom:16 }}>
              Store these somewhere safe. Each can be used once to sign in if you lose your authenticator.
            </p>
            <div style={{ background:"var(--surface2)", border:"1px solid var(--border2)", borderRadius:"var(--r)", padding:16, marginBottom:16 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 16px", fontFamily:"var(--mono)", fontSize:13, color:"var(--text2)" }}>
                {setupRecoveryCodes.map((code, i) => (
                  <div key={i} style={{ letterSpacing:"0.05em" }}>{code}</div>
                ))}
              </div>
            </div>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const blob = new Blob(
                  [`FinBackup — recovery codes for ${email}\n\nEach code may be used once.\n\n${setupRecoveryCodes.join("\n")}\n`],
                  { type: "text/plain" },
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "finbackup-recovery-codes.txt";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              }}
              style={{ width:"100%", padding:"10px", marginBottom:8, fontSize:13 }}
            >
              Download codes as .txt
            </button>
            <button
              className="btn btn-primary"
              onClick={() => { window.location.href = "/"; }}
              style={{ width:"100%", padding:"10px" }}
            >
              I've saved them — continue
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SignupPage() {
  const navigate = useNavigate();

  // Step: account form → MFA QR/verify → recovery codes
  const [step, setStep] = useState<"form" | "mfa" | "recovery">("form");

  // Form fields
  const [orgName, setOrgName]   = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");

  // MFA fields
  const [signupToken, setSignupToken] = useState("");
  const [otpauthUri, setOtpauthUri]   = useState("");
  const [mfaSecret, setMfaSecret]     = useState("");
  const [mfaCode, setMfaCode]         = useState("");

  // Recovery codes returned after successful verification
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, org_name: orgName }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Signup failed"); return; }

      setSignupToken(data.signup_token);
      setOtpauthUri(data.mfa.otpauth_uri);
      setMfaSecret(data.mfa.secret);
      setStep("mfa");
    } catch (err: any) {
      setError(err.message ?? "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/signup-complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_token: signupToken, code: mfaCode }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Verification failed"); return; }

      localStorage.setItem("fb_access_token", data.access_token);
      localStorage.setItem("fb_refresh_token", data.refresh_token);
      setRecoveryCodes(data.recovery_codes ?? []);
      setStep("recovery");
    } catch (err: any) {
      setError(err.message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width:"100%", padding:"9px 12px", border:"1px solid var(--border2)", borderRadius:"var(--r)", fontSize:14, fontFamily:"var(--sans)", outline:"none", background:"var(--surface)" };
  const labelStyle = { fontSize:12, fontFamily:"var(--mono)", color:"var(--text3)", display:"block" as const, marginBottom:6, textTransform:"uppercase" as const, letterSpacing:"0.06em" };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--surface2)", padding:"24px 0" }}>
      <div style={{ width: step === "recovery" ? 440 : 380, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--r2)", padding:"32px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:28 }}>
          <div style={{ width:32, height:32, background:"var(--ink)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ width:20, height:20, background:"var(--teal)", borderRadius:4 }} />
          </div>
          <div>
            <div style={{ fontWeight:500, fontSize:15, letterSpacing:"-0.3px" }}>FinBackup</div>
            <div style={{ fontSize:11, fontFamily:"var(--mono)", color:"var(--text3)" }}>Financial Data Protection</div>
          </div>
        </div>

        {error && <div style={{ background:"var(--red-bg)", color:"#791F1F", fontSize:13, padding:"10px 14px", borderRadius:"var(--r)", marginBottom:16 }}>{error}</div>}

        {step === "form" && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:6, letterSpacing:"-0.3px" }}>Create your account</h2>
            <p style={{ fontSize:13, color:"var(--text3)", marginBottom:20 }}>
              Two-factor authentication is required to protect your financial data.
            </p>
            <form onSubmit={handleCreate} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={labelStyle}>Organisation name</label>
                <input type="text" value={orgName} onChange={e=>setOrgName(e.target.value)} required autoFocus style={inputStyle} placeholder="Acme Accountants Ltd" />
              </div>
              <div>
                <label style={labelStyle}>Work email</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={8} style={inputStyle} placeholder="At least 8 characters" />
              </div>
              <div>
                <label style={labelStyle}>Confirm password</label>
                <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} required minLength={8} style={inputStyle} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading} style={{ marginTop:8, width:"100%", padding:"10px" }}>
                {loading ? "Creating account…" : "Continue"}
              </button>
            </form>
            <div style={{ marginTop:18, textAlign:"center", fontSize:13, color:"var(--text3)" }}>
              Already have an account?{" "}
              <a href="/login" onClick={(e) => { e.preventDefault(); navigate("/login"); }} style={{ color:"var(--teal)", textDecoration:"none", fontWeight:500 }}>
                Sign in
              </a>
            </div>
          </>
        )}

        {step === "mfa" && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:6, letterSpacing:"-0.3px" }}>Set up two-factor authentication</h2>
            <p style={{ fontSize:13, color:"var(--text3)", marginBottom:16 }}>
              Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy), then enter the 6-digit code to activate your account.
            </p>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}>
              <img
                alt="MFA QR code"
                width={180}
                height={180}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpauthUri)}`}
                style={{ border:"1px solid var(--border2)", borderRadius:"var(--r)", padding:6, background:"#fff" }}
              />
            </div>
            <div style={{ fontSize:12, color:"var(--text3)", textAlign:"center", marginBottom:4 }}>
              Can't scan? Enter this secret manually:
            </div>
            <div style={{ fontFamily:"var(--mono)", fontSize:12, textAlign:"center", letterSpacing:"0.05em", wordBreak:"break-all", color:"var(--text2)", marginBottom:18 }}>
              {mfaSecret}
            </div>
            <form onSubmit={handleVerify} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={labelStyle}>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  required
                  style={{ ...inputStyle, fontSize: 18, fontFamily:"var(--mono)", textAlign:"center", letterSpacing:"0.15em" }}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading || mfaCode.length < 6} style={{ marginTop:8, width:"100%", padding:"10px" }}>
                {loading ? "Verifying…" : "Verify & finish"}
              </button>
            </form>
          </>
        )}

        {step === "recovery" && (
          <>
            <h2 style={{ fontSize:18, fontWeight:500, marginBottom:6, letterSpacing:"-0.3px" }}>Save your recovery codes</h2>
            <p style={{ fontSize:13, color:"var(--text3)", marginBottom:16 }}>
              Store these codes somewhere safe. Each code can be used once to sign in if you lose access to your authenticator.
            </p>
            <div style={{ background:"var(--surface2)", border:"1px solid var(--border2)", borderRadius:"var(--r)", padding:16, marginBottom:16 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 16px", fontFamily:"var(--mono)", fontSize:13, color:"var(--text2)" }}>
                {recoveryCodes.map((code, i) => (
                  <div key={i} style={{ letterSpacing:"0.05em" }}>{code}</div>
                ))}
              </div>
            </div>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const blob = new Blob(
                  [`FinBackup — recovery codes for ${email}\n\nEach code may be used once.\n\n${recoveryCodes.join("\n")}\n`],
                  { type: "text/plain" },
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "finbackup-recovery-codes.txt";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              }}
              style={{ width:"100%", padding:"10px", marginBottom:8, fontSize:13 }}
            >
              Download codes as .txt
            </button>
            <button
              className="btn btn-primary"
              onClick={() => { window.location.href = "/"; }}
              style={{ width:"100%", padding:"10px" }}
            >
              I've saved them — continue to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}

import { useState, Component } from "react";

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; message: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(err: Error) {
    return { hasError: true, message: err.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface2)" }}>
          <div style={{ textAlign: "center", maxWidth: 420, padding: 32 }}>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.25 }}>⚠</div>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Something went wrong</h2>
            <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 20 }}>
              An unexpected error occurred. Try refreshing the page — if the problem persists, contact support.
            </p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"          element={<LoginPage />} />
          <Route path="/signup"         element={<SignupPage />} />
          <Route path="/invite/:token"  element={<InvitePage />} />
          <Route path="/*" element={
            <RequireAuth>
              <Routes>
                <Route index element={<Dashboard />} />
                <Route path="history"    element={<HistoryPage />} />
                <Route path="restore"    element={<RestorePage />} />
                <Route path="connectors" element={<ConnectorsPage />} />
                <Route path="schedules"  element={<SchedulesPage />} />
                <Route path="users"      element={<UsersPage />} />
                <Route path="audit"      element={<AuditPage />} />
                <Route path="settings"   element={<SettingsPage />} />
                <Route path="admin/tenants"      element={<AdminTenantsPage />} />
                <Route path="admin/tenants/:id"  element={<AdminTenantDetailPage />} />
              </Routes>
            </RequireAuth>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="shell">
      <div style={{ display:"none" }} />
      <div className="main-column">
        <div className="topbar"><h1>{title}</h1></div>
        <div className="page" style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flex:1, gap:12, color:"var(--text3)" }}>
          <div style={{ fontSize:36, opacity:0.2 }}>⬡</div>
          <p style={{ fontSize:15, fontWeight:500, color:"var(--text2)" }}>{title}</p>
          <p style={{ fontSize:13 }}>Coming in Phase 2</p>
        </div>
      </div>
    </div>
  );
}
